import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Navbar } from '@/components/Navbar'
import apiService from '@/api/service'
import type { StoreSettingsRequest } from '@/types/api'

export const StoreSetting = (): JSX.Element => {
    const navigate = useNavigate()
    const location = useLocation()
    const [formData, setFormData] = useState({
        store_name: '',
        store_addr: '',
        comment_url: ''
    })
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string>('')
    const [isExporting, setIsExporting] = useState(false)
    const [exportError, setExportError] = useState<string>('')

    // 检测是否从 login 进入（首次设定）
    // 只有从 login 进来才显示单个"完成設定"按钮，其他情况都显示两个按钮
    const isFromLogin = location.state?.from === 'login' || new URLSearchParams(location.search).get('from') === 'login'

    // 加載時讀取已儲存的店家資訊
    useEffect(() => {
        const storeInfo = apiService.getStoreInfo()
        if (storeInfo) {
            console.log('讀取已儲存的店家資訊:', storeInfo)
            setFormData({
                store_name: storeInfo.name || '',
                store_addr: storeInfo.addr || '',
                comment_url: storeInfo.comment_url || ''
            })
        }
    }, [])

    const handleInputChange = (field: string) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: e.target.value
        }))
        // 清除錯誤訊息
        if (error) setError('')
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        console.log('========== 開始提交店家設定表單 ==========')
        console.log('表單資料:', formData)

        // 驗證輸入
        if (!formData.store_name.trim()) {
            console.log('❌ 驗證失敗: 餐廳名稱為空')
            setError('請輸入餐廳名稱')
            return
        }
        if (!formData.store_addr.trim()) {
            console.log('❌ 驗證失敗: 餐廳地址為空')
            setError('請輸入餐廳地址')
            return
        }
        if (!formData.comment_url.trim()) {
            console.log('❌ 驗證失敗: Google Maps URL 為空')
            setError('請輸入 Google Maps URL')
            return
        }

        console.log('✅ 表單驗證通過')

        setIsLoading(true)
        setError('')

        try {
            const storeCode = apiService.getCurrentStoreCode()
            console.log('當前 store_code:', storeCode)

            if (!storeCode) {
                console.log('❌ 找不到 store_code，需要重新登入')
                setError('登入資訊遺失，請重新登入')
                navigate('/login')
                return
            }

            const request: StoreSettingsRequest = {
                store_code: storeCode,
                store_name: formData.store_name,
                comment_url: formData.comment_url,
                store_addr: formData.store_addr
            }

            console.log('========== 呼叫儲存店家 API 前的參數 ==========')
            console.log('API Endpoint: POST /api/store/create-and-run')
            console.log('請求參數（按 API 文件順序）:')
            console.log('  1. store_code:', storeCode)
            console.log('  2. store_name:', formData.store_name)
            console.log('  3. comment_url:', formData.comment_url)
            console.log('  4. store_addr:', formData.store_addr)
            console.log('完整請求物件:', request)
            console.log('JSON 格式:')
            console.log(JSON.stringify(request, null, 2))
            console.log('===============================================')

            const response = await apiService.setStoreSettings(request)

            console.log('========== API 響應結果 ==========')
            console.log('狀態:', response.status)
            console.log('訊息:', response.message)
            console.log('時間戳:', response.timestamp)
            console.log('返回的 store_code:', response.store_code)
            console.log('完整響應:', response)
            console.log('==================================')

            if (response.status === 'success') {
                console.log('✅ 店家設定成功')

                // 保存店家資訊到本地存儲
                const storeInfoToSave = {
                    code: storeCode,
                    name: formData.store_name,
                    addr: formData.store_addr,
                    comment_url: formData.comment_url
                }
                console.log('========== 保存店家資訊到本地 ==========')
                console.log('準備保存的店家資訊:', storeInfoToSave)
                apiService.saveStoreInfo(storeInfoToSave)
                console.log('✅ 店家資訊已保存到 localStorage')
                console.log('=======================================')

                // 根據來源決定跳轉頁面
                if (isFromLogin) {
                    // 從 login 進入（首次設定），跳轉到等待頁面
                    console.log('從 login 進入（首次設定），跳轉到等待頁面')
                    navigate('/waiting')
                } else {
                    // 其他情況（如從 dashboard 進入），返回 dashboard
                    console.log('從其他頁面進入，返回 dashboard')
                    navigate('/dashboard')
                }
            } else {
                console.log('❌ 店家設定失敗:', response.message)
                setError(response.message || '設定失敗')
            }
        } catch (error) {
            console.error('========== API 調用發生錯誤 ==========')
            console.error('錯誤類型:', error instanceof Error ? error.constructor.name : typeof error)
            console.error('錯誤訊息:', error instanceof Error ? error.message : String(error))
            console.error('完整錯誤:', error)
            console.error('=====================================')
            setError('設定失敗，請稍後再試')
        } finally {
            setIsLoading(false)
        }
    }

    // 處理匯出按鈕
    const handleExport = async () => {
        const storeCode = apiService.getCurrentStoreCode()
        if (!storeCode) {
            setExportError('找不到店家代碼，請重新登入')
            return
        }

        setIsExporting(true)
        setExportError('')

        try {
            await apiService.downloadComments(storeCode)
        } catch (err) {
            console.error('匯出失敗:', err)
            setExportError(err instanceof Error ? err.message : '匯出失敗，請稍後再試')
        } finally {
            setIsExporting(false)
        }
    }

    // 處理返回按鈕
    const handleBack = () => {
        navigate('/dashboard')
    }

    // 處理登出
    const handleLogout = () => {
        console.log('========== 執行登出 ==========')
        // 清除所有登入資料和店家資訊
        apiService.logout()
        console.log('✅ 已清除所有登入資料')
        console.log('跳轉到登入頁面')
        console.log('=============================')
        // 跳轉到登入頁面
        navigate('/login')
    }

    return (
        <div
            className="bg-white w-full min-h-screen flex flex-col overflow-hidden"
            data-model-id="1:2140"
        >
            {/* 使用 Navbar 組件 */}
            <Navbar />

            {/* Main Content - 响应式，支持滚动 */}
            <div className="w-full flex-1 relative bg-[#aeceb7] overflow-y-auto flex items-start justify-center px-4 pt-16 pb-8 md:py-0">
                <div className="relative md:absolute md:top-[97px] w-full max-w-[95%] md:max-w-[1450px] min-h-fit md:h-[884px] flex flex-col items-center justify-center bg-[#e8d080] rounded-[30px] md:rounded-[50px_50px_0px_0px] border-[3px] md:border-[5px] border-solid border-white py-8 md:py-0 mt-8 mb-8 md:mt-0 md:mb-0">
                    <form onSubmit={handleSubmit} className="w-full max-w-[95%] md:max-w-[1160px] relative flex flex-col items-center justify-center gap-8 md:gap-[25px] py-8 md:py-[28px] px-4 md:px-0 bg-[#eaeade] rounded-[15px] md:rounded-[20px]">
                        <div className="relative w-full max-w-full md:max-w-[1160px] min-h-fit md:h-[560px] flex flex-col gap-6 md:gap-0">
                            {/* 錯誤訊息 - 响应式 */}
                            {error && (
                                <div className="mb-4 md:absolute md:top-0 md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-8 px-4 py-2 bg-red-500 text-white rounded-lg text-xs md:text-sm z-10 text-center">
                                    {error}
                                </div>
                            )}

                            {/* 餐廳名稱 - 响应式 */}
                            <div className="relative md:absolute md:top-[50px] md:left-0 w-full md:w-[1160px] flex flex-col md:flex-row gap-2 md:gap-0 md:h-[60px]">
                                <div className="flex items-center md:justify-center md:mt-3 md:w-28 md:h-9 md:ml-[23px] [font-family:'Noto_Sans_TC',Helvetica] font-medium text-[#373737] text-base md:text-xl tracking-[1.00px] md:tracking-[2.00px] leading-6 md:leading-9">
                                    餐廳名稱 :
                                </div>

                                <div className="w-full md:w-[989px] h-[50px] md:h-[60px] flex bg-white rounded-[10px] overflow-hidden border-2 md:border-[3px] border-solid border-[#efdfac]">
                                    <input
                                        type="text"
                                        value={formData.store_name}
                                        onChange={handleInputChange('store_name')}
                                        placeholder="請輸入餐廳名稱"
                                        className="flex-1 h-full px-3 md:px-4 [font-family:'Noto_Sans_TC',Helvetica] font-medium text-[#373737] text-base md:text-xl tracking-[1.00px] md:tracking-[2.00px] leading-6 md:leading-9 bg-transparent border-none outline-none placeholder:text-[#929084]"
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>


                            {/* 餐廳地址 - 响应式 */}
                            <div className="relative md:absolute md:top-[130px] md:left-0 w-full md:w-[1160px] overflow-hidden flex flex-col md:flex-row gap-2 md:gap-0 md:h-[60px]">
                                <div className="flex items-center md:justify-center md:mt-3 md:w-28 md:h-9 md:ml-[23px] [font-family:'Noto_Sans_TC',Helvetica] font-medium text-[#373737] text-base md:text-xl tracking-[1.00px] md:tracking-[2.00px] leading-6 md:leading-9">
                                    餐廳地址 :
                                </div>

                                <div className="h-[50px] md:h-[60px] w-full md:w-[989px] flex bg-white rounded-[10px] overflow-hidden border-2 md:border-[3px] border-solid border-[#efdfac]">
                                    <input
                                        type="text"
                                        value={formData.store_addr}
                                        onChange={handleInputChange('store_addr')}
                                        placeholder="請輸入餐廳地址"
                                        className="flex-1 h-full px-3 md:px-4 [font-family:'Noto_Sans_TC',Helvetica] font-medium text-[#373737] text-base md:text-xl tracking-[1.00px] md:tracking-[2.00px] leading-6 md:leading-9 bg-transparent border-none outline-none placeholder:text-[#929084]"
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                            {/* Google Maps URL - 响应式 */}
                            <div className="relative md:absolute md:top-[210px] md:left-0 w-full md:w-[1160px] flex flex-col md:flex-row gap-2 md:gap-0 md:h-[301px] overflow-hidden">
                                <div className="flex items-center md:justify-center md:mt-[12.5px] md:w-28 md:h-9 md:ml-[23px] [font-family:'Noto_Sans_TC',Helvetica] font-medium text-[#373737] text-base md:text-xl tracking-[1.00px] md:tracking-[2.00px] leading-6 md:leading-9">
                                    填寫URL :
                                </div>

                                <div className="h-[150px] md:h-[301px] w-full md:w-[989px] flex bg-white rounded-[10px] overflow-hidden border-2 md:border-[3px] border-solid border-[#efdfac]">
                                    <textarea
                                        value={formData.comment_url}
                                        onChange={handleInputChange('comment_url')}
                                        placeholder="請輸入 Google Maps URL"
                                        className="flex-1 h-full p-3 md:p-4 resize-none [font-family:'Noto_Sans_TC',Helvetica] font-medium text-[#373737] text-sm md:text-lg tracking-[0.50px] md:tracking-[1.00px] leading-5 md:leading-6 bg-transparent border-none outline-none placeholder:text-[#929084]"
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 按鈕區域 - 响应式 */}
                        {isFromLogin ? (
                            /* 從 login 進入（首次設定）：只顯示完成設定按鈕 - 响应式 */
                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`w-full max-w-[350px] md:max-w-[420px] h-[50px] md:h-[55px] rounded-[50px] md:rounded-[267.22px] overflow-hidden transition-colors flex items-center justify-center ${isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#929084] hover:bg-[#7a7a6b]'
                                    }`}
                            >
                                <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-lg md:text-2xl text-center tracking-[6.00px] md:tracking-[24.00px] leading-none whitespace-nowrap">
                                    {isLoading ? '設定中...' : '完成設定'}
                                </div>
                            </button>
                        ) : (
                            /* 其他情況（如從 dashboard 進入）：顯示返回 + 確認設定 + 登出 三個按鈕 - 响应式 */
                            <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4 w-full sm:w-auto">
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    disabled={isLoading}
                                    className="w-full sm:w-[140px] md:w-[160px] h-[50px] md:h-[55px] rounded-[50px] md:rounded-[267.22px] overflow-hidden transition-colors bg-[#929084] hover:bg-[#7a7a6b] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-lg md:text-2xl text-center tracking-[3.00px] md:tracking-[6.00px] leading-none whitespace-nowrap">
                                        返回
                                    </div>
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full sm:w-[140px] md:w-[160px] h-[50px] md:h-[55px] rounded-[50px] md:rounded-[267.22px] overflow-hidden transition-colors bg-[#aeceb7] hover:bg-[#9dbd9f] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-lg md:text-2xl text-center tracking-[3.00px] md:tracking-[6.00px] leading-none whitespace-nowrap">
                                        {isLoading ? '設定中...' : '確認設定'}
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    disabled={isLoading}
                                    className="w-full sm:w-[140px] md:w-[160px] h-[50px] md:h-[55px] rounded-[50px] md:rounded-[267.22px] overflow-hidden transition-colors bg-[#e8d080] hover:bg-[#d9c070] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-lg md:text-2xl text-center tracking-[3.00px] md:tracking-[6.00px] leading-none whitespace-nowrap">
                                        登出
                                    </div>
                                </button>
                            </div>
                        )}

                        {/* ── 虛線分隔線（在 form 卡片內）── */}
                        <div className="w-full px-4 md:px-8">
                            <div className="w-full border-t-2 border-dashed border-[#c9b86a]" />
                        </div>

                        {/* ── 匯出資料列（在 form 卡片內）── */}
                        <div className="w-full px-4 md:px-8">
                            {/* 匯出錯誤訊息 */}
                            {exportError && (
                                <div className="mb-3 px-4 py-2 bg-red-500 text-white rounded-lg text-xs md:text-sm text-center">
                                    {exportError}
                                </div>
                            )}

                            {/* 白底圓矩框 + 陰影 */}
                            <div className="w-full bg-white rounded-[12px] md:rounded-[16px] shadow-md px-5 md:px-8 py-4 md:py-5 flex items-center justify-between">
                                {/* 左側：標題 + ℹ 圖示 + 說明文字 */}
                                <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                                    <span className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#373737] text-base md:text-xl tracking-[2px] md:tracking-[3px] leading-none">
                                        匯出資料
                                    </span>
                                    {/* ℹ 圓形圖示 */}
                                    <div className="flex-shrink-0 flex items-center justify-center w-[18px] h-[18px] md:w-[20px] md:h-[20px] rounded-full border-2 border-[#929084]">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#929084" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-[10px] h-[10px] md:w-[12px] md:h-[12px]">
                                            <line x1="12" y1="8" x2="12" y2="8" />
                                            <line x1="12" y1="12" x2="12" y2="16" />
                                        </svg>
                                    </div>
                                    {/* 永遠顯示的說明文字 */}
                                    <span className="[font-family:'Noto_Sans_TC',Helvetica] font-normal text-[#929084] text-xs md:text-sm tracking-[0.5px] leading-none">
                                        點擊匯出將匯出此系統相關文檔
                                    </span>
                                </div>

                                {/* 右側：匯出按鈕 */}
                                <button
                                    type="button"
                                    onClick={handleExport}
                                    disabled={isExporting}
                                    className={`flex-shrink-0 w-[90px] md:w-[120px] h-[40px] md:h-[48px] rounded-[50px] overflow-hidden transition-colors flex items-center justify-center ${
                                        isExporting
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-[#929084] hover:bg-[#7a7a6b]'
                                    }`}
                                >
                                    <span className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-base md:text-xl tracking-[4px] md:tracking-[6px] leading-none whitespace-nowrap">
                                        {isExporting ? '匯出中' : '匯 出'}
                                    </span>
                                </button>
                            </div>
                        </div>


                    </form>

                </div>

                {/* 页面标题 - 响应式，固定在顶部 */}
                <div className="fixed md:absolute top-[68px] md:top-[33px] left-1/2 -translate-x-1/2 [font-family:'Noto_Sans_TC',Helvetica] font-black text-white text-xl md:text-4xl text-center tracking-[3.00px] md:tracking-[7.20px] leading-7 md:leading-9 whitespace-nowrap px-4 z-10">
                    設定店家資料
                </div>
            </div>
        </div>
    )
}
